import { Component, computed, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { RealmService } from '@/app/services/realm.service';
import { CategoryWithdrawConfig, RealmConfig } from '@/lib-backend';
import { isSkillSpecific } from '@/app/utils/inheritance';
import { CategoryWithdrawForm } from '@/app/components/category-config/category-withdraw-form';
import { OverridableSection } from '@/app/components/category-config/overridable-section';

@Component({
    selector: 'app-website-info-withdraw',
    standalone: true,
    imports: [FormsModule, Select, Button, Dialog, CategoryWithdrawForm, OverridableSection],
    templateUrl: './withdraw-tab.html',
})
export class WithdrawTab {
    readonly realm = input.required<RealmConfig>();
    readonly draft = model.required<CategoryWithdrawConfig>();
    readonly overridden = model.required<boolean>();

    protected readonly realmService = inject(RealmService);

    /** Withdraw only exists on skill-based realms; null otherwise (tab is hidden then). */
    protected readonly section = computed(() => {
        const cs = this.realm().CategorySpecific;
        return isSkillSpecific(cs) ? cs.Withdraw : null;
    });

    protected readonly dirty = computed(() => {
        const s = this.section();
        if (!s) return false;
        return this.overridden() !== s.IsOverridden ||
            JSON.stringify(this.draft()) !== JSON.stringify(s.Effective);
    });

    protected readonly importableRealms = computed(() =>
        this.realmService.realms().filter(r => r.RealmId !== this.realm().RealmId)
    );

    protected importDialogVisible = signal(false);
    protected importSourceRealmId = signal<string | null>(null);
    protected importLoading = signal(false);

    protected onToggle(value: boolean): void {
        this.overridden.set(value);
        const s = this.section();
        if (!value && s) {
            this.draft.set(clone(s.Inherited));
        }
    }

    protected resetAll(): void {
        const s = this.section();
        if (!s) return;
        this.draft.set(clone(s.Effective));
        this.overridden.set(s.IsOverridden);
    }

    protected openImportDialog(): void {
        this.realmService.loadRealms();
        this.importSourceRealmId.set(null);
        this.importDialogVisible.set(true);
    }

    protected async applyImport(): Promise<void> {
        const realmId = this.importSourceRealmId();
        if (!realmId) return;
        this.importLoading.set(true);
        try {
            const result = await this.realmService.getRealm(realmId);
            const cs = result?.config.CategorySpecific;
            if (cs && isSkillSpecific(cs)) {
                this.draft.set(clone(cs.Withdraw.Effective));
                this.overridden.set(true);
            }
        } finally {
            this.importLoading.set(false);
        }
        this.importDialogVisible.set(false);
    }
}

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}
